import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  memo,
} from "react";
import Card from "assets/Card.png";
import { Grid, useTheme } from "@mui/material";
import CheckIcon from "assets/Check.png";
import UncheckIcon from "assets/UnCheck.png";
import { FormControl, FormControlLabel, Radio } from "@mui/material";
import {
  FullModeInnerWrapper,
  FullModeWrapper,
  GridItem,
  PaymentButton,
  PaymentStyledInner,
  PaymentStyledRadioGroup,
  PaymentStyledText,
} from "components/styles";
import { TOContext } from "context/TContext";
import { FormProvider } from "react-hook-form";
import CreditCardForwardForm from "./TOCreditCardForwardForm";
import AdyenCardForm from "./AdyenCardForm";
import { usePaymentContext } from "context/TPaymentContext";
import CustomTextInput from "components/Shared/CustomTextField";
import CustomCheckbox from "components/Shared/CustomCheckbox";
import CustomMobilePhone from "components/Shared/CustomMobilePhone";
import {
  PaymentSystemContext,
  PAYMENT_SYSTEM_TYPES,
} from "context/PaymentSystemContext";
import StripeCreditCardDetail from "components/CreditCardForm/StripeForm/StripeCreditCardDetail";

const CARD_INITIAL_VALUES = {
  cardNumberComplete: false,
  expiredComplete: false,
  cvcComplete: false,
  cardNumberError: null,
  expiredError: null,
  cvcError: null,
};

// Extracted form fields component for better reusability
const UserInfoForm = memo(({ theme }) => (
  <Grid container spacing={1} sx={{ mt: theme.spacing(2) }}>
    <Grid item xs={12}>
      <CustomTextInput
        sx={{ width: "100%" }}
        name="firstName"
        label="First Name *"
      />
    </Grid>
    <Grid item xs={12}>
      <CustomTextInput
        sx={{ width: "100%" }}
        name="lastName"
        label="Last Name *"
      />
    </Grid>
    <Grid item xs={12}>
      <CustomMobilePhone
        sx={{ width: "100%" }}
        label="Phone Number *"
        name="phone"
      />
    </Grid>
    <Grid item xs={12}>
      <CustomTextInput sx={{ width: "100%" }} name="email" label="Email *" />
    </Grid>
    <GridItem item xs={12} sm={12} md={12} lg={12}>
      <CustomCheckbox
        id=""
        name="acceptUpdatesPromotionEmails"
        defaultChecked={true}
        label="Text me with order updates, news and offers"
        disabled={false}
      />
    </GridItem>
  </Grid>
));

UserInfoForm.displayName = "UserInfoForm";

// Extracted radio button component
const CreditCardRadioButton = memo(
  ({ payWithCreditCard, CREDIT_CARD, theme, onSelect }) => (
    <FullModeWrapper
      onClick={onSelect}
      sx={{
        cursor: "pointer",
        border:
          payWithCreditCard === CREDIT_CARD &&
          `2px solid ${theme.palette.primary.main}`,
      }}
    >
      <FullModeInnerWrapper>
        <FormControl sx={{ width: "100%" }}>
          <PaymentStyledRadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue="full"
            name="radio-buttons-group"
            value={payWithCreditCard}
          >
            <FormControlLabel
              value="credit card"
              control={
                <Radio
                  icon={<img src={UncheckIcon} alt="uncheck" />}
                  checkedIcon={<img src={CheckIcon} alt="checked" />}
                />
              }
              label={
                <PaymentStyledInner>
                  <img src={Card} alt="card" />
                  <PaymentStyledText>Credit Card</PaymentStyledText>
                </PaymentStyledInner>
              }
              sx={{
                width: "100%",
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  fontWeight: 700,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                },
                "& .MuiButtonBase-root": { pr: 0 },
              }}
            />
          </PaymentStyledRadioGroup>
        </FormControl>
      </FullModeInnerWrapper>
    </FullModeWrapper>
  )
);

CreditCardRadioButton.displayName = "CreditCardRadioButton";

const TOCardPayment = ({
  userInfo,
  errorCard,
  setErrorCard,
  tokenForward,
  setHasTokenForward,
  loadingPay,
  handleSubmit,
  isWebView,
  handleSubmitStripe,
  handleSubmitAdyen,
  adyenCardReady,
  adyenPaymentCompleted,
  loadingAdyenSession,
  dropinContainerId,
  adyenStatus,
  isLoading,
}) => {
  const theme = useTheme();
  const { CREDIT_CARD, payWithCreditCard, handleSelect, isRefundDueShow } =
    useContext(TOContext);
  const { paymentSystem } = useContext(PaymentSystemContext);
  const { forwardTokenizeLoading } = usePaymentContext();

  const [cardError, setCardError] = useState(false);
  const [cardDetails, setCardDetails] = useState(CARD_INITIAL_VALUES);

  // Memoize the card error handler to prevent unnecessary re-renders
  const handleCardError = useCallback((error) => {
    setCardError(error);
  }, []);

  // Auto-select credit card in WebView
  useEffect(() => {
    if (isWebView) {
      handleSelect(CREDIT_CARD);
    }
  }, [isWebView, CREDIT_CARD, handleSelect]);

  // Memoize the onSelect handler to prevent unnecessary re-renders
  const handleCardSelect = useCallback(() => {
    handleSelect(CREDIT_CARD);
  }, [handleSelect, CREDIT_CARD]);

  // Memoize disabled states for better performance
  const disabledPayForward = useMemo(() => {
    return (
      !userInfo.formState.isValid ||
      errorCard ||
      !tokenForward ||
      isRefundDueShow ||
      loadingPay
    );
  }, [
    userInfo.formState.isValid,
    errorCard,
    tokenForward,
    isRefundDueShow,
    loadingPay,
  ]);

  const disabledPayStripe = useMemo(() => {
    return (
      cardError ||
      !cardDetails.cardNumberComplete ||
      !cardDetails.expiredComplete ||
      !cardDetails.cvcComplete ||
      cardDetails.cardNumberError ||
      cardDetails.expiredError ||
      cardDetails.cvcError ||
      !userInfo.formState.isValid
    );
  }, [
    cardError,
    cardDetails.cardNumberComplete,
    cardDetails.expiredComplete,
    cardDetails.cvcComplete,
    cardDetails.cardNumberError,
    cardDetails.expiredError,
    cardDetails.cvcError,
    userInfo.formState.isValid,
  ]);

  // Memoize the payment system check
  const isForwardPayment = useMemo(() => {
    return paymentSystem === PAYMENT_SYSTEM_TYPES.FORWARD;
  }, [paymentSystem]);

  const isStripePayment = useMemo(() => {
    return paymentSystem === PAYMENT_SYSTEM_TYPES.STRIPE;
  }, [paymentSystem]);

  const isAdyenPayment = useMemo(() => {
    return paymentSystem === PAYMENT_SYSTEM_TYPES.ADYEN;
  }, [paymentSystem]);

  const disabledPayAdyen = useMemo(() => {
    return (
      !userInfo.formState.isValid ||
      isRefundDueShow ||
      loadingPay ||
      loadingAdyenSession ||
      (!adyenCardReady && !adyenPaymentCompleted)
    );
  }, [
    adyenCardReady,
    adyenPaymentCompleted,
    isRefundDueShow,
    loadingAdyenSession,
    loadingPay,
    userInfo.formState.isValid,
  ]);

  // Memoize the button disabled state
  const isPaymentDisabled = useMemo(() => {
    if (isAdyenPayment) return disabledPayAdyen;
    return isForwardPayment ? disabledPayForward : disabledPayStripe;
  }, [
    disabledPayAdyen,
    disabledPayForward,
    disabledPayStripe,
    isAdyenPayment,
    isForwardPayment,
  ]);

  // Memoize the loading state
  const isPaymentLoading = useMemo(() => {
    return (
      forwardTokenizeLoading ||
      loadingPay ||
      isLoading ||
      loadingAdyenSession
    );
  }, [forwardTokenizeLoading, isLoading, loadingAdyenSession, loadingPay]);

  // Memoize the form submission handler
  const onSubmit = useCallback(
    (data) => {
      if (isAdyenPayment) {
        handleSubmitAdyen?.(data);
        return;
      }
      if (isStripePayment) {
        handleSubmitStripe(data);
        return;
      }
      handleSubmit(data);
    },
    [
      handleSubmit,
      handleSubmitAdyen,
      handleSubmitStripe,
      isAdyenPayment,
      isStripePayment,
    ]
  );

  return (
    <>
      <CreditCardRadioButton
        payWithCreditCard={payWithCreditCard}
        CREDIT_CARD={CREDIT_CARD}
        theme={theme}
        onSelect={handleCardSelect}
      />

      <FormProvider {...userInfo}>
        <form onSubmit={userInfo.handleSubmit(onSubmit)}>
          {payWithCreditCard === CREDIT_CARD && (
            <UserInfoForm theme={theme} />
          )}

          {isAdyenPayment ? (
            <AdyenCardForm
              dropinContainerId={dropinContainerId}
              loading={loadingAdyenSession}
              visible={payWithCreditCard === CREDIT_CARD}
              status={adyenStatus}
            />
          ) : isStripePayment && payWithCreditCard === CREDIT_CARD ? (
            <StripeCreditCardDetail
              handleCardError={handleCardError}
              cardDetails={cardDetails}
              setCardDetails={setCardDetails}
            />
          ) : (
            !isStripePayment && (
              <CreditCardForwardForm
                handleCardError={setErrorCard}
                setTokenForward={setHasTokenForward}
              />
            )
          )}

          {payWithCreditCard === CREDIT_CARD && (
            <PaymentButton
              type="submit"
              loading={isPaymentLoading}
              disabled={isPaymentDisabled}
            >
              Pay
            </PaymentButton>
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default memo(TOCardPayment);
